import * as React from 'react'
import * as Redux from 'react-redux'
import styled from 'styled-components'

import { Lang } from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { Status } from '@owl-nest/redux-wrapper'
import { useLazyInitialLoad } from '@owl-nest/hooks'
import * as plume from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

import { loadProjectFaq } from '../../../../actions/faq'
import * as impactAction from '../../../../actions/impact'
import { DisplayMode } from '../../../context'
import { useIndice } from '../hooks/useIndice'
import { useNavigation } from '../../../hooks/useNavigation'
import { PATHS } from '../../../paths'
import { RootState, AppState } from '../../../../reducers'
import * as projectModel from '../../../models/project'

import { AccordionWrapper } from './AccordionWrapper'
import { CreateButton } from '../components/CreateButton'
import { EditButton } from '../components/EditButton'

import * as UFE from '../../../../UFE'

/* This component is currently used on project tab and FAQ tab but some behaviours and one style are different:
- Project tab: Project FAQ is opened on desktop and closed on mobile + "Read full FAQ" plume.Link
- FAQ tab: Project FAQ is always opened + "Read more" plume.Link + specific style on mobile for one title
*/
export function ProjectFaq(): React.ReactElement<unknown> {
  const { isOpen, updateOpenIndices } = useIndice()
  const navigation = useNavigation()
  const userConfig = env.useUserEnv()

  const dispatch = Redux.useDispatch()
  const { faq, impact, project: projectState } = Redux.useSelector<RootState, AppState>((state) => state.application)
  const displayMode = React.useContext(DisplayMode)
  const project = projectState.data
  const isEditMode = displayMode === 'edit'
  const projectToken = projectState.token
  const lang = project.lang as Lang

  const isProjectChallengeSet =
    impact.data?.challenges &&
    Object.keys(impact.data.challenges).length !== 0 &&
    Object.values(impact.data.challenges).find((value) => !!value)

  const hasProjectFAQ = faq?.faq?.length > 0

  const isProjectTab = navigation.is(PATHS.PROJECT)
  const toFAQ = (): void => navigation.to(PATHS.FAQ)
  const loadMoreFaq = (): void => {
    faq.next && dispatch(loadProjectFaq(project.id, { pagination: `${faq.next}&lang=${userConfig.lang}` }))
  }
  const risksHelperUrl = model.i18n.get(UFE.LINKS.risksHelper)

  const faqWrapperRef = React.useRef<HTMLDivElement>(null)
  useLazyInitialLoad({
    target: faqWrapperRef,
    load: () => {
      if (faq.status.load === Status.PRISTINE) {
        dispatch(
          loadProjectFaq(
            project.id,
            { pagination: `?limit=5&lang=${userConfig.lang}`, forceClean: true },
            projectToken,
          ),
        )
      }
      if (impact.status.load.status === Status.PRISTINE) {
        dispatch(impactAction.load(project.id, projectToken))
      }
    },
  })

  React.useEffect(() => {
    if (impact.data && window.matchMedia('(min-width: 768px)').matches) {
      if (!isOpen('challenges')) {
        updateOpenIndices('challenges')
      }
    }
  }, [impact])

  return (
    <div ref={faqWrapperRef}>
      {faq.status.load === Status.PENDING || (impact.status.load.status === Status.PRISTINE && <plume.Spinner />)}
      {faq.status.load !== Status.PRISTINE &&
        (!hasProjectFAQ &&
        !isProjectChallengeSet &&
        isEditMode &&
        !model.project.refused<projectModel.ExtraFields>(project) ? (
          <CreateButtonWrapper $isProjectTab={isProjectTab}>
            <CreateButton
              createHandler={() => {
                document.location.href = `/projects/${project.id}/edit/faq/#tab-content-${userConfig.lang}`
              }}
              mode="small"
              title={<plume.styles.heading.XS>{t('Add a FAQ')}</plume.styles.heading.XS>}
            />
          </CreateButtonWrapper>
        ) : (
          <>
            {(hasProjectFAQ || isProjectChallengeSet) && (
              <FAQWrapper $open={isOpen('content') || !isProjectTab}>
                <TitleContent
                  onClick={() => {
                    updateOpenIndices('content')
                  }}
                  $isProjectTab={isProjectTab}
                >
                  <plume.styles.heading.L as="h2">{t('Questions about the project')}</plume.styles.heading.L>
                  {isOpen('content') ? (
                    <plume.glyphs.stroke.CaretUp size={18} />
                  ) : (
                    <plume.glyphs.stroke.CaretDown size={18} />
                  )}
                </TitleContent>
                <AccordionWrapper open={isOpen('content') || !isProjectTab} openOnDesktop={true}>
                  <>
                    {isEditMode && !model.project.refused<projectModel.ExtraFields>(project) && (
                      <EditButtonWrapper>
                        <EditButton link={`/projects/${project.id}/edit/faq/#tab-content-${userConfig.lang}`} />
                      </EditButtonWrapper>
                    )}
                    {isProjectChallengeSet && impact.data && (
                      <Question key="challenges">
                        <Title
                          onClick={() => {
                            updateOpenIndices('challenges')
                          }}
                        >
                          <TitleFlex>
                            <plume.styles.heading.XXXS>
                              {t('What are the risks associated with the project?')}
                            </plume.styles.heading.XXXS>
                          </TitleFlex>
                          {isOpen('challenges') ? (
                            <plume.glyphs.stroke.CaretUp size={18} />
                          ) : (
                            <plume.glyphs.stroke.CaretDown size={18} />
                          )}
                        </Title>
                        <AccordionWrapper open={isOpen('challenges')}>
                          <AnswerWrapper $textSize="medium">
                            <p>{impact.data ? model.impact.getChallenges(impact.data, lang) : null}</p>
                          </AnswerWrapper>
                          <plume.styles.copy.S>
                            <plume.Link
                              kind="secondary"
                              href={risksHelperUrl}
                              target="_blank"
                              rel="nooponer noreferrer"
                            >
                              {t('Learn more about risks for Ulule projects')}
                            </plume.Link>
                          </plume.styles.copy.S>
                        </AccordionWrapper>
                      </Question>
                    )}
                    {faq?.faq?.map((item, index) => (
                      <Question key={index}>
                        <Title
                          onClick={() => {
                            updateOpenIndices(index.toString())
                          }}
                        >
                          <QuestionContent>
                            <TitleFlex>
                              {item.question_author ? (
                                <plume.UserLabel
                                  href={item.question_author.absolute_url}
                                  avatar={model.user.avatar(item.question_author, '40x40')}
                                  avatarSize="extra-small"
                                  name={item.question_author.name}
                                />
                              ) : (
                                <plume.styles.heading.XXXS>
                                  {model.faq.getQuestion(item, lang)}
                                </plume.styles.heading.XXXS>
                              )}
                            </TitleFlex>
                            {item.question_author && (
                              <plume.styles.heading.XXXS>{model.faq.getQuestion(item, lang)}</plume.styles.heading.XXXS>
                            )}
                          </QuestionContent>
                          {isOpen(index.toString()) ? (
                            <plume.glyphs.stroke.CaretUp size={18} />
                          ) : (
                            <plume.glyphs.stroke.CaretDown size={18} />
                          )}
                        </Title>

                        <AccordionWrapper open={isOpen(index.toString())}>
                          {item.answer_author ? (
                            <Answer>
                              <plume.UserLabel
                                href={item.answer_author.absolute_url}
                                avatar={model.user.avatar(item.answer_author, '40x40')}
                                avatarSize="extra-small"
                                name={item.answer_author.name}
                                tag={model.user.tag(item.answer_author, item.answer_author.role)}
                              />

                              <AnswerWrapper
                                dangerouslySetInnerHTML={{ __html: model.faq.getAnswer(item, lang) || '' }}
                              ></AnswerWrapper>
                            </Answer>
                          ) : (
                            <AnswerWrapper
                              dangerouslySetInnerHTML={{ __html: model.faq.getAnswer(item, lang) || '' }}
                            ></AnswerWrapper>
                          )}
                        </AccordionWrapper>
                      </Question>
                    ))}
                    {(faq?.next || (isProjectTab && hasProjectFAQ)) && (
                      <LoadMoreWrapper>
                        <plume.styles.button.CallToAction kind="secondary" onClick={isProjectTab ? toFAQ : loadMoreFaq}>
                          {isProjectTab ? t('Read full FAQ') : t('See more')}
                        </plume.styles.button.CallToAction>
                      </LoadMoreWrapper>
                    )}
                  </>
                </AccordionWrapper>
              </FAQWrapper>
            )}
          </>
        ))}
    </div>
  )
}

const FAQWrapper = styled.div<{ $open: boolean }>`
  position: relative;
  border-bottom: ${({ $open }) => (!$open ? `1px solid ${plume.COLORS.GREY_SHADE_5}` : 'none')};
  margin-bottom: 30px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    border-bottom: none;
  }
`

const Answer = styled.div`
  margin-top: 15px;
  border-bottom: 1px solid ${plume.COLORS.GREY_SHADE_5};
  margin-left: 40px;

  &:last-child {
    border-bottom: 0px solid #fff;
  }
`

const Question = styled.div`
  border-bottom: 1px solid ${plume.COLORS.GREY_SHADE_5};
  padding: 17px 0px;

  &:last-child {
    border-bottom: 0px solid #fff;
  }
`

const QuestionContent = styled.div`
  width: 100%;
`

const AnswerWrapper = styled(plume.WysiwygContent)`
  margin-top: 4px;
`

const Title = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 1;

  svg {
    margin-left: auto;
    color: ${plume.COLORS.GREY_SHADE_7};
  }

  ${plume.styles.heading.XXXS} {
    margin-top: 4px;
  }
`

const TitleFlex = styled.div`
  display: flex;
  flex: 1;

  ${plume.styles.heading.XXXS} {
    margin-top: 0;
    margin-right: 5px;
  }
`

const TitleContent = styled.div<{ $isProjectTab: boolean }>`
  position: relative;

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.GREY_SHADE_7};
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    display: ${({ $isProjectTab }) => ($isProjectTab ? 'inline-block' : 'none')};
  }

  ${plume.styles.heading.L} {
    font-size: ${({ $isProjectTab }) => ($isProjectTab ? '26px' : '30px')};
    font-weight: ${({ $isProjectTab }) => ($isProjectTab ? '500' : '900')};
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    ${plume.glyphs.Icon} {
      display: none;
    }

    ${plume.styles.heading.L} {
      font-size: 30px;
      font-weight: 900;
    }
  }
`

const LoadMoreWrapper = styled.div`
  margin-top: 45px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin: 45px auto 0px;
    width: 260px;
  }

  ${plume.styles.button.Button} {
    padding: 9px 35px;
  }
`

const CreateButtonWrapper = styled.div<{ $isProjectTab: boolean }>`
  border: 1px solid ${plume.COLORS.PRIMARY_SAND};
  display: flex;
  flex-direction: row;
  height: auto;
  //justify-content: baseline;
  margin-bottom: ${({ $isProjectTab }) => ($isProjectTab ? '0px' : '30px')};
  padding: 21px 18px;
  text-align: center;
  transition: 0.3s ease;

  &:hover {
    box-shadow: ${plume.EFFECTS.SHADOW_1};
  }
`

const EditButtonWrapper = styled.div`
  right: 5px;
  top: 0px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    position: absolute;
    right: 0px;
  }
`
